import React, { createContext, useEffect, useState, useContext } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from './firebase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [readOnlyOffline, setReadOnlyOffline] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth,
      currentUser => {
        if (currentUser?.uid) {
          setUser(currentUser);
          localStorage.setItem('lastUserId', currentUser.uid);
          setReadOnlyOffline(false);
        } else {
          setUser(null);
        }
        setLoading(false);
      },
      error => {
        // Firebase failed (likely offline); try fallback
        const fallbackUid = localStorage.getItem('lastUserId');
        if (fallbackUid) {
          setUser({ uid: fallbackUid });
          setReadOnlyOffline(true);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const signInWithEmail = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email, password) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logOut = async () => {
    await signOut(auth);
    localStorage.removeItem('lastUserId');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        readOnlyOffline,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        logOut
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
